import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Autocomplete from 'react-autocomplete';
import assign from 'object-assign';
import qs from 'qs';
import { Glyphicon } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import client from '../../utils/rest/AdminClient';
import DuplicateLocationModal from './duplicate-location-modal';
import { getClassName } from '../../utils/ui/classes';
import { renderMenu } from '../v2/dropdowns/Menu';

// Error boundary to catch react-autocomplete errors when refs become null
// This can happen when focus shifts due to enforceFocus={false} on modals
class AutocompleteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: false }; // Don't show error state, just recover
  }

  componentDidCatch(error, errorInfo) {
    // Log but don't crash - this handles the ownerDocument null error
    if (error?.message?.includes('ownerDocument')) {
      console.warn(
        'LocationAutosuggest: Caught expected error during focus transition',
        error,
      );
    } else {
      console.error('LocationAutosuggest error:', error, errorInfo);
    }
  }

  render() {
    return this.props.children;
  }
}

class LocationAutosuggest extends Component {
  static propTypes = {
    id: PropTypes.string.isRequired,
    value: PropTypes.string,
    inputStyle: PropTypes.object,
    placeholderText: PropTypes.string,
    searchField: PropTypes.string,
    callbackSelectedItem: PropTypes.func.isRequired,
    onChange: PropTypes.func,
    searchType: PropTypes.object,
    readOnly: PropTypes.bool,
    autoHighlight: PropTypes.bool,
  };

  static defaultProps = {
    value: '',
    placeholderText: 'START_TYPING_TO_SEARCH_FOR_A_LOCATION',
    autoHighlight: true,
  };

  constructor(props) {
    super(props);
    this.state = {
      value: this.props.value,
      suggestions: [],
      showDuplicateLocationsModal: false,
      duplicateAddress: null,
      searchType: this.props.searchType
        ? this.props.searchType
        : {
            includeCargomaticAddresses: 1,
            includeGoogleAddresses: 1,
          },
    };
    this._isMounted = false;
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.value !== nextProps.value) {
      const { value } = nextProps;
      this.setState({ value });
    }
  }

  updateItem = (value, item) => {
    value = this.props.searchField === 'name' ? item.name : item.address;

    const duplicateAddress = this.state.suggestions.filter((location) => {
      return location.address === item.address;
    });

    if (duplicateAddress.length > 1) {
      return this.setState({
        showDuplicateLocationsModal: true,
        duplicateAddress,
      });
    }

    this.setState({ value }, () => {
      this.props.callbackSelectedItem(item);
    });
  };

  updateDuplicate = (item) => {
    const value = this.props.searchField === 'name' ? item.name : item.address;
    this.setState({ value, showDuplicateLocationsModal: false }, () => {
      this.props.callbackSelectedItem(item);
    });
  };

  handleHide = () => {
    this.setState({ showDuplicateLocationsModal: false });
  };

  renderItem = (item) => {
    const { searchField } = this.props;

    return (
      <a
        id={item.address}
        className="list-group-item"
        style={{ cursor: 'pointer' }}
        key={item.address}
      >
        <p className="list-group-item-heading">
          {item._id !== undefined ? (
            <Glyphicon glyph="star" data-testid="icon-saved-location" />
          ) : (
            <Glyphicon glyph="map-marker" data-testid="icon-google-location" />
          )}
          {searchField === 'name' || !searchField ? (
            <span>
              <b>&nbsp;{item.name}&nbsp;</b>
            </span>
          ) : (
            <span>&nbsp;{item.name}</span>
          )}
          {searchField === 'address' ? (
            <span>
              <b>&nbsp;{item.address}</b>
            </span>
          ) : (
            <span>{item.address}</span>
          )}
        </p>
      </a>
    );
  };

  renderMenu = (items, _value, style) => {
    const { dropdownStyle } = this.props;
    const defaultStyle = {
      position: 'absolute',
      top: 45,
      left: 0,
      zIndex: 100,
      background: 'white',
    };
    return renderMenu(items, {
      ...style,
      ...defaultStyle,
      ...this.menuStyle,
      ...dropdownStyle,
    });
  };

  autocompleteLocationRequest = debounce(autocompleteLocationRequest, 500);

  render() {
    const { searchField, t } = this.props;

    return (
      <div className="relative flex-1 w-full">
        <AutocompleteErrorBoundary>
          <Autocomplete
            inputProps={{
              id: this.props.id,
              className: getClassName(
                'w-full form-field',
                this.props.className,
              ),
              style: this.props.inputStyle || { width: '100%' },
              placeholder: t(this.props.placeholderText),
              readOnly: this.props.readOnly,
              onClick: (e) => {
                // Guard against react-autocomplete's handleInputClick error
                // when refs.input is null during component unmount
                if (!this._isMounted) {
                  e.stopPropagation();
                }
              },
            }}
            wrapperStyle={{}}
            autoHighlight={this.props.autoHighlight}
            value={this.state.value || ''}
            items={this.state.suggestions}
            getItemValue={(item) => item.name}
            onSelect={(value, item) => this.updateItem(value, item)}
            onChange={(event, value) => {
              this.setState({ value });
              clearTimeout(this.timeout);
              if (this.props.onChange) {
                this.props.onChange(value);
              }
              this.timeout = setTimeout(() => {
                if (!value) {
                  this.setState({ suggestions: [] });
                  return;
                }
                this.autocompleteLocationRequest(
                  value,
                  searchField,
                  this.state.searchType,
                  (suggestions) => {
                    if (suggestions) {
                      this.setState({ suggestions });
                    }
                  },
                );
              }, 500);
            }}
            menuStyle={{
              borderRadius: '3px',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)',
              background: 'rgba(255, 255, 255, 0.9)',
              padding: '2px 0',
              fontSize: '90%',
              position: 'fixed',
              overflow: 'auto',
              maxHeight: '50%',
              zIndex: 1,
            }}
            renderMenu={this.renderMenu}
            renderItem={this.renderItem}
          />
        </AutocompleteErrorBoundary>
        <div>
          {this.state.showDuplicateLocationsModal && (
            <DuplicateLocationModal
              onHide={this.handleHide}
              show={this.state.showDuplicateLocationsModal}
              addresses={this.state.duplicateAddress}
              onSelectLocation={this.updateDuplicate}
            />
          )}
        </div>
      </div>
    );
  }
}

function autocompleteLocationRequest(value, field, searchType, cb) {
  getLocationSuggestions(value, field, searchType).then((res) => {
    cb(res.entity.filter((l) => l.address));
  });
}

function getLocationSuggestions(value, field, searchType) {
  const path = '/api/v2/locations/autocomplete';
  const params = {
    input: value,
  };

  if (field !== '') {
    params.only = field;
  }
  const queryParams = assign(
    {},
    {
      input: value,
      includeGoogleAddresses: searchType.includeGoogleAddresses,
      includeCargomaticAddresses: searchType.includeCargomaticAddresses,
    },
    params || {},
  );
  const queryString = qs.stringify(queryParams);
  return client({
    method: 'GET',
    path: `${path}?${queryString}`,
  });
}

export default withTranslation()(LocationAutosuggest);
